import {
  afterRenderEffect,
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  ElementRef,
  inject,
  OnDestroy,
  signal,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatButton, MatIconButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { MatMenu, MatMenuItem, MatMenuTrigger } from '@angular/material/menu';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatTableModule } from '@angular/material/table';
import { MatTooltip } from '@angular/material/tooltip';
import { ActivatedRoute } from '@angular/router';
import {
  catchError,
  EMPTY,
  filter,
  finalize,
  forkJoin,
  map,
  of,
  switchMap,
  tap,
  throwError,
  withLatestFrom,
} from 'rxjs';
import { FileService, HasPermissionDirective, PermissionService, triggerIfSlow } from '@ipg/ngx-frontend-toolset';
import { PermissionSyncService } from '../../services/permission-sync.service';
import { ConfirmHandler } from '../../components/dialog/dialog.model';
import { DialogService } from '../../components/dialog/dialog.service';
import { HeaderComponent } from '../../components/header/header.component';
import { InteractionSelectionDialogService } from '../../components/interaction-selection-dialog/services/interaction-selection-dialog.service';
import { MediaPickerDialogService } from '../../components/media-picker-dialog/services/media-picker-dialog.service';
import { EmptyStatePlaceholderComponent } from '../../components/placeholders/empty-state-placeholder/empty-state-placeholder.component';
import { TagSelectionDialogService } from '../../components/tag-selection-dialog/services/tag-selection-dialog.service';

import { GlobalLoadersService } from '../../global-loaders.service';
import { MapFromPipe } from '../../pipes/map-from.pipe';
import { MediaContext } from '../../models/media-context.model';
import { MediaItem } from '../../models/media.model';
import { MediaService } from '../../services/media.service';

import {
  SignalingAudioBlockConfig,
  SignalingBlock,
  SignalingBlockType,
  SignalingImageBlockConfig,
} from './client-signaling.model';
import { SignalingBlockAudioConfigDialogComponent } from './components/signaling-block-audio-config-dialog/signaling-block-audio-config-dialog.component';
import { SignalingBlockAudioTableComponent } from './components/signaling-block-audio-table/signaling-block-audio-table.component';
import { SignalingBlockAvatarComponent } from './components/signaling-block-avatar/signaling-block-avatar.component';
import { SignalingBlockFolderTableComponent } from './components/signaling-block-folder-table/signaling-block-folder-table.component';
import { SignalingBlockImageConfigDialogComponent } from './components/signaling-block-image-config-dialog/signaling-block-image-config-dialog.component';
import { SignalingBlockImageTableComponent } from './components/signaling-block-image-table/signaling-block-image-table.component';
import { SignalingBlockInteractionTableComponent } from './components/signaling-block-interaction-table/signaling-block-interaction-table.component';
import { SignalingBlockStoreService } from './services/signaling-block-store.service';

interface SignalingBlockMenuItem {
  type: SignalingBlockType;
  value: string;
  icon: string;
}

function assertDefined<T>(value: T): asserts value is NonNullable<T> {
  if (value === undefined || value === null) {
    throw new Error(`${value} is not defined`);
  }
}

const MAX_BLOCK_LENGTH = 5;

@Component({
  selector: 'app-client-signaling',
  imports: [
    MatButton,
    MatIcon,
    MatIconButton,
    MatMenu,
    MatMenuItem,
    MatMenuTrigger,
    MatTableModule,
    MatTooltip,
    SignalingBlockAvatarComponent,
    SignalingBlockAudioTableComponent,
    SignalingBlockFolderTableComponent,
    SignalingBlockImageTableComponent,
    SignalingBlockInteractionTableComponent,
    MapFromPipe,
    EmptyStatePlaceholderComponent,
    HasPermissionDirective,
  ],
  templateUrl: './client-signaling.component.html',
  styleUrl: './client-signaling.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ClientSignalingComponent implements OnDestroy {
  private static readonly LOADER_CALLER_ID = 'client_signaling_loader_caller';
  private static readonly BLOCK_CREATE_LOADER_CALLER_ID = 'block_create_loader_caller';
  private static readonly BLOCK_TOGGLE_LOADER_CALLER_ID = 'block_toggle_loader_caller';

  private readonly _dialog = inject(DialogService);
  private readonly _file = inject(FileService);
  private readonly _route = inject(ActivatedRoute);
  private readonly _snackBar = inject(MatSnackBar);
  private readonly _mediaService = inject(MediaService);

  private readonly _blockStore = inject(SignalingBlockStoreService);
  private readonly _mediaPicker = inject(MediaPickerDialogService);
  private readonly _interactionSelectionDialog = inject(
    InteractionSelectionDialogService
  );
  private readonly _tagSelectionDialog = inject(TagSelectionDialogService);
  private readonly _permissionService = inject(PermissionService);
  private readonly _permissionSync = inject(PermissionSyncService);

  private readonly _isImportingMedia = signal(false);

  protected readonly canUpdateScm = computed(() =>
    this._permissionService.hasPermission('scm:update')
  );

  protected readonly canReadInteraction = computed(() =>
    this._permissionService.hasPermission('interaction:read')
  );

  private readonly _blocksContainer =
    viewChild<ElementRef<HTMLElement>>('blocksContainer');

  private readonly _pendingScrollToNewBlock = signal(false);

  private readonly _clientId = this._route.snapshot.parent!.paramMap.get('clientId');

  private readonly _clientId$ = of(this._clientId).pipe(
    filter((clientId) => clientId != null)
  );

  constructor() {
    afterRenderEffect(() => {
      if (this._pendingScrollToNewBlock()) {
        this._pendingScrollToNewBlock.set(false);
        const container = this._blocksContainer()?.nativeElement;
        const lastBlock = container?.lastElementChild;
        lastBlock?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    });

    this._clientId$
      .pipe(
        switchMap((clientId) =>
          this._blockStore.loadBlocks(clientId).pipe(
            tap({
              error: () =>
                this._dialog.alert({
                  title: `Foutmelding`,
                  message: `Er is iets misgegaan bij het ophalen van de historie. Probeer het later nog eens`,
                  confirmText: `OK`,
                }),
            }),
            takeUntilDestroyed(this._destroyRef),
            triggerIfSlow(() =>
              this._globalLoaders.setActive(
                ClientSignalingComponent.LOADER_CALLER_ID,
                HeaderComponent.LOADER_ID
              )
            ),
            finalize(() =>
              this._globalLoaders.setInactive(
                ClientSignalingComponent.LOADER_CALLER_ID,
                HeaderComponent.LOADER_ID
              )
            )
          )
        )
      )
      .subscribe();
  }

  private readonly _destroyRef = inject(DestroyRef);

  private readonly _globalLoaders = inject(GlobalLoadersService);

  protected readonly maxBlockReferenceLength = 20;

  protected readonly blocks = computed(() =>
    this._blockStore.blocks().map((value) => this.mapBlock(value))
  );

  protected readonly canAddBlock = computed(
    () => this.blocks().length < MAX_BLOCK_LENGTH
  );

  protected readonly blockCreateMenuItems: SignalingBlockMenuItem[] = [
    {
      icon: 'image',
      value: 'Afbeeldingen',
      type: 'image',
    },
    {
      icon: 'music_note',
      value: 'Audio',
      type: 'audio',
    },
    {
      icon: 'record_voice_over',
      value: 'Interacties',
      type: 'interaction',
    },
    {
      icon: 'sell',
      value: 'Tags',
      type: 'folder',
    },
  ];

  protected readonly blockConfigTypeMap = {
    audio: {
      random: 'Willekeurig',
      playlist: 'Afspeellijst',
    },
    image: {
      random: 'Willekeurig',
      slideshow: 'Slideshow',
    },
  };

  ngOnDestroy(): void {
    this._blockStore.clearBlocks();
  }

  mapBlock(block: SignalingBlock): SignalingBlock {
    return {
      ...block,
      avatarUrl: block.avatarFileId
        ? this._file.get(block.avatarFileId).href
        : undefined,
    };
  }

  canBlockActivate(block: SignalingBlock) {
    return block.avatarFileId != null && block.data.length > 0;
  }

  handleBlockCreate(type: SignalingBlockType) {
    of(type)
      .pipe(
        map((type) => ({ type })),
        withLatestFrom(this._clientId$),
        switchMap(([data, clientId]) =>
          this._blockStore.addBlock(clientId, data).pipe(
            tap(() => this._pendingScrollToNewBlock.set(true)),
            takeUntilDestroyed(this._destroyRef),
            triggerIfSlow(() =>
              this._globalLoaders.setActive(
                ClientSignalingComponent.BLOCK_CREATE_LOADER_CALLER_ID,
                HeaderComponent.LOADER_ID
              )
            ),
            catchError((error) => {
              this._dialog.alert({
                title: 'Foutmelding',
                message:
                  'Er is iets misgegaan bij het maken van een signalering blok. Probeer het later nog eens',
                confirmText: 'OK',
              });

              return throwError(() => error);
            }),
            finalize(() =>
              this._globalLoaders.setInactive(
                ClientSignalingComponent.BLOCK_CREATE_LOADER_CALLER_ID,
                HeaderComponent.LOADER_ID
              )
            )
          )
        )
      )
      .subscribe();
  }

  handleBlockRemove(block: SignalingBlock) {
    if (block.active) {
      this.showDisallowChangeDialog();

      return;
    }

    this._dialog.confirm({
      title: 'Blok verwijderen?',
      message:
        'Als je dit blok verwijdert, is het niet meer zichtbaar voor de cliënt en worden de referenties automatisch losgekoppeld. Weet je zeker dat je dit blok wilt verwijderen?',
      confirmText: 'Verwijderen',
      cancelText: 'Annuleren',
      onConfirm: () =>
        of(block._id).pipe(
          withLatestFrom(this._clientId$),
          switchMap(([blockId, clientId]) =>
            this._blockStore.removeBlock(clientId, blockId).pipe(
              catchError(() => {
                this._dialog.alert({
                  title: 'Foutmelding',
                  message:
                    'Er is iets misgegaan bij het verwijderen van het blok. Probeer het later nog eens',
                  confirmText: 'OK',
                });

                return EMPTY;
              })
            )
          )
        ),
    });
  }

  handleBlockActiveChange(blockId: SignalingBlock['_id'], active: boolean) {
    assertDefined(this._clientId);

    of(this._clientId)
      .pipe(
        switchMap((clientId) =>
          this._blockStore.updateActive(clientId, blockId, { active }).pipe(
            takeUntilDestroyed(this._destroyRef),
            triggerIfSlow(() =>
              this._globalLoaders.setActive(
                ClientSignalingComponent.BLOCK_TOGGLE_LOADER_CALLER_ID,
                HeaderComponent.LOADER_ID
              )
            ),
            catchError((error) => {
              this._dialog.alert({
                title: 'Foutmelding',
                message:
                  'Er is iets misgegaan bij het activeren/deactiveren van het blok. Probeer het later nog eens',
                confirmText: 'OK',
              });

              return throwError(() => error);
            })
          )
        ),
        finalize(() =>
          this._globalLoaders.setInactive(
            ClientSignalingComponent.BLOCK_TOGGLE_LOADER_CALLER_ID,
            HeaderComponent.LOADER_ID
          )
        )
      )
      .subscribe();
  }

  handleBlockAvatarChange(block: SignalingBlock) {
    if (block.active) {
      this.showDisallowChangeDialog();

      return;
    }

    if (!this._requirePermission('media:read')) return;

    this._mediaPicker.open(
      (result) =>
        of(result).pipe(
          filter((result) => result != null),
          switchMap((mediaItem) =>
            this._importIfNeeded(mediaItem, this._clientId!, {
              scope: 'client',
              clientId: this._clientId!,
            })
          ),
          map((importedMedia) => ({ fileId: importedMedia.fileId })),
          withLatestFrom(this._clientId$),
          switchMap(([data, clientId]) =>
            this._blockStore.updateAvatar(clientId, block._id, data).pipe(
              catchError((error) => {
                this._dialog.alert({
                  title: 'Foutmelding',
                  message:
                    'Er is iets misgegaan bij het bewerken van de avatar. Probeer het later nog eens',
                  confirmText: 'OK',
                });

                return throwError(() => error);
              })
            )
          )
        ),
      {
        type: 'image',
        context: { scope: 'client', clientId: this._clientId! },
      }
    );
  }

  handleBlockItemAdd(block: SignalingBlock) {
    if (block.active) {
      this.showDisallowChangeDialog();

      return;
    }

    if (block.data.length >= this.maxBlockReferenceLength) {
      console.warn('Unable to add item. Max items reached');

      return;
    }

    if (block.type === 'audio' || block.type === 'image') {
      if (!this._requirePermission('media:read')) return;

      this._mediaPicker.open(
        (result) =>
          of(result).pipe(
            filter((result) => result != null),
            switchMap((mediaItems) =>
              this._importMediaItems(mediaItems, this._clientId!)
            ),
            map((importedItems) => importedItems.map((value) => value.id)),
            map((referenceIds) => ({ referenceIds })),
            withLatestFrom(this._clientId$),
            switchMap(([data, clientId]) =>
              this._blockStore.addReference(clientId, block._id, data).pipe(
                catchError(() => {
                  this._dialog.alert({
                    title: 'Foutmelding',
                    message:
                      'Er is iets misgegaan bij het toevoegen van een referentie. Probeer het later nog eens',
                    confirmText: 'OK',
                  });

                  return of(null);
                })
              )
            )
          ),
        {
          type: block.type,
          multi: true,
          initialSelectionCount: block.data.length,
          maxSelectionLimit: this.maxBlockReferenceLength,
          context: { scope: 'client', clientId: this._clientId! },
        }
      );
    } else if (block.type === 'interaction') {
      assertDefined(this._clientId);
      if (!this._requirePermission('interaction:read')) return;

      this._interactionSelectionDialog.open(
        (result) =>
          of(result).pipe(
            filter((result) => result != null),
            map((result) => result.map((value) => value.correlationId)),
            map((referenceIds) => ({
              referenceIds,
            })),
            withLatestFrom(this._clientId$),
            switchMap(([data, clientId]) =>
              this._blockStore.addReference(clientId, block._id, data).pipe(
                catchError(() => {
                  this._dialog.alert({
                    title: 'Foutmelding',
                    message:
                      'Er is iets misgegaan bij het toevoegen van een referentie. Probeer het later nog eens',
                    confirmText: 'OK',
                  });

                  return of(null);
                })
              )
            )
          ),
        {
          clientId: this._clientId,
          status: ['published'],
          mode: 'match',
          initialSelectionCount: block.data.length,
          maxSelectionLimit: this.maxBlockReferenceLength,
          storageKey: 'signaling-interaction-selection',
        }
      );
    } else if (block.type === 'folder') {
      if (!this._requirePermission('tag:read')) return;

      this._tagSelectionDialog.open(
        (result) =>
          of(result).pipe(
            filter((result) => result != null),
            map((result) => result.map((value) => value.id)),
            map((referenceIds) => ({ referenceIds })),
            withLatestFrom(this._clientId$),
            switchMap(([data, clientId]) =>
              this._blockStore.addReference(clientId, block._id, data).pipe(
                catchError(() => {
                  this._dialog.alert({
                    title: 'Foutmelding',
                    message:
                      'Er is iets misgegaan bij het toevoegen van een referentie. Probeer het later nog eens',
                    confirmText: 'OK',
                  });

                  return of(null);
                })
              )
            )
          ),
        {
          initialSelectionCount: block.data.length,
          maxSelectionLimit: this.maxBlockReferenceLength,
          disabledLabels: block.data.map((blockItem) => blockItem.label),
          clientId: this._clientId!,
        }
      );
    }
  }

  handleBlockItemRemove(block: SignalingBlock, linkId: string) {
    if (block.active) {
      this.showDisallowChangeDialog();

      return;
    }

    this._dialog.confirm({
      title: 'Referentie verwijderen?',
      message:
        'Weet je zeker dat je de referentie wilt verwijderen? Deze actie kan niet ongedaan worden gemaakt',
      cancelText: 'Annuleren',
      confirmText: 'Verwijderen',
      onConfirm: () =>
        this._clientId$.pipe(
          switchMap((clientId) =>
            this._blockStore.deleteReference(clientId, block._id, linkId).pipe(
              catchError(() => {
                this._dialog.alert({
                  title: 'Foutmelding',
                  message:
                    'Er is iets misgegaan bij het verwijderen van een referentie. Probeer het later nog eens',
                  confirmText: 'OK',
                });

                return of(null);
              })
            )
          )
        ),
    });
  }

  handleBlockConfigChange(block: Exclude<SignalingBlock, { type: 'interaction' }>) {
    if (block.active) {
      this.showDisallowChangeDialog();

      return;
    }

    const callback: ConfirmHandler<
      SignalingAudioBlockConfig | SignalingImageBlockConfig
    > = (result) =>
      of(result).pipe(
        withLatestFrom(this._clientId$),
        switchMap(([data, clientId]) =>
          this._blockStore.updateBlockConfig(clientId, block._id, data).pipe(
            catchError((error) => {
              this._dialog.alert({
                title: 'Foutmelding',
                message:
                  'Er is iets misgegaan bij het opslaan van de configuratie. Probeer het later nog eens',
                confirmText: 'OK',
              });

              return throwError(() => error);
            })
          )
        )
      );

    if (block.type === 'audio') {
      this.handleAudioBlockConfigDialog(callback, block.config);
    } else if (block.type === 'image') {
      this.handleImageBlockConfigDialog(callback, block.config);
    }
  }

  handleAudioBlockConfigDialog(
    callback: ConfirmHandler<SignalingAudioBlockConfig>,
    config: SignalingAudioBlockConfig
  ) {
    return this._dialog.open(SignalingBlockAudioConfigDialogComponent, {
      ...config,
      onConfirm: callback,
    });
  }

  handleImageBlockConfigDialog(
    callback: ConfirmHandler<SignalingImageBlockConfig>,
    config: SignalingImageBlockConfig
  ) {
    return this._dialog.open(SignalingBlockImageConfigDialogComponent, {
      ...config,
      onConfirm: callback,
    });
  }

  /**
   * Whether `permission` is held, explaining it when not. Each source a block
   * draws from (media, interactions, tags) asks for its own permission on top
   * of the `scm:update` that shows the buttons.
   */
  private _requirePermission(permission: string): boolean {
    if (this._permissionService.hasPermission(permission)) return true;
    this._permissionSync.explainMissing([permission]);
    return false;
  }

  showDisallowChangeDialog() {
    this._dialog.alert({
      title: 'Bewerken niet mogelijk',
      message:
        'Het is niet mogelijk om avatars te bewerken, referenties toe te voegen of te verwijderen of een configuratie te veranderen wanneer een blok actief is',
      confirmText: 'OK',
    });
  }

  private _importMediaItems(
    mediaItems: MediaItem[],
    clientId: string
  ) {
    const context: MediaContext = { scope: 'client', clientId };

    if (mediaItems.length === 0) {
      return of([]);
    }

    const importObservables = mediaItems.map((item) =>
      this._importIfNeeded(item, clientId, context)
    );

    return forkJoin(importObservables);
  }

  private _importIfNeeded(
    mediaItem: MediaItem,
    clientId: string | null,
    context: MediaContext
  ) {
    if (!clientId || mediaItem.scope === 'client') {
      return of(mediaItem);
    }

    this._isImportingMedia.set(true);

    return this._mediaService.import(mediaItem.id, context).pipe(
      map((importedMedia) => ({
        ...mediaItem,
        id: importedMedia._id,
        fileId: importedMedia.fileId,
        scope: 'client' as const,
      })),
      finalize(() => this._isImportingMedia.set(false)),
      catchError((error) => {
        this._snackBar.open('Media import failed', '', { duration: 3000 });
        return throwError(() => error);
      })
    );
  }
}
